import React, { useState } from 'react'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import { CircularProgress, IconButton } from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import { requiredStringMinThree } from '../../../../utils/validation'
import { subjectApiSlice } from '../../../../features/subject/subjectSlice'
import { SubjectState } from '../../../../features/types'
import AddSubjectDialogContent from './AddSubjectDialogContent'

interface Props {
    editSubject?: SubjectState
    editMode?: boolean
}

const AddSubjectDialog = ({ editSubject, editMode }: Props) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = React.useState(false)

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const [createSubject, { isLoading, error }] =
        subjectApiSlice.useCreateSubjectMutation()

    const [updateSubject, { isLoading: updateLoading }] =
        subjectApiSlice.useUpdateSubjectMutation()

    const [image, setImage] = useState(null)

    const subject: SubjectState = {
        titleKz: editSubject?.titleKz || '',
        titleRu: editSubject?.titleRu || '',
        descriptionKz: editSubject?.descriptionKz || '',
        descriptionRu: editSubject?.descriptionRu || '',
        logo: '',
    }

    const formData = new FormData()

    const validationSchema = yup.object().shape({
        titleKz: requiredStringMinThree({ formatMessage }),
        titleRu: requiredStringMinThree({ formatMessage }),
        descriptionKz: requiredStringMinThree({ formatMessage }),
        descriptionRu: requiredStringMinThree({ formatMessage }),
        logo: !editSubject ? yup.mixed().required('Сурет жүктеңіз') : null,
    })

    const formik = useFormik({
        initialValues: subject,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            formData.append('titleKz', formik.values.titleKz)
            formData.append('titleRu', formik.values.titleRu)
            formData.append('descriptionKz', formik.values.descriptionKz)
            formData.append('descriptionRu', formik.values.descriptionRu)
            formData.append('logo', formik.values.logo)

            if (editSubject) {
                handleUpdateSubject()
            } else {
                handleCreateSubject()
            }
        },
    })

    const handleCreateSubject = async () => {
        try {
            await createSubject(formData).unwrap()
            formik.resetForm()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {
            console.log('createSubject', error)
        }
    }

    const handleUpdateSubject = async () => {
        formData.append('id', `${editSubject?.id}`)

        try {
            await updateSubject(formData).unwrap()
            formik.resetForm()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {
            toast.error('Произошла ошибка')
        }
    }

    function onImageChange(e) {
        formik.setFieldValue('logo', e.target.files[0])
        setImage(URL.createObjectURL(e.target.files[0]))
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: '#f3f4f5' }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addSubject' })}
                </Button>
            )}

            <Dialog
                maxWidth={'xl'}
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editSubject' })
                        : formatMessage({ id: 'addSubject' })}
                </DialogTitle>

                <AddSubjectDialogContent
                    editSubject={editSubject}
                    onImageChange={onImageChange}
                    formik={formik}
                    image={image}
                />

                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isLoading || updateLoading}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isLoading || updateLoading}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isLoading || updateLoading ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddSubjectDialog
